// Global packages and components
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { clsx } from 'clsx'

// Types
import type { ReactNode } from 'react'

interface Props {
  open: boolean
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  bgDark?: boolean
  disableClose?: boolean
  fullHeightMobile?: boolean
  setOpen: (e: boolean) => void
  onClose?: () => void
}

// Main export
const Modal = ({
  open,
  children,
  size = 'lg',
  bgDark = false,
  disableClose,
  fullHeightMobile = false,
  setOpen,
  onClose,
}: Props) => {
  const handleClose = () => {
    !disableClose ? setOpen(false) : undefined
  }

  return (
    <Dialog
      open={open}
      onClose={onClose ? onClose : handleClose}
      className="relative z-50"
    >
      <>
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <div
          className={clsx(
            'fixed inset-0 flex w-screen items-start justify-center overflow-y-auto',
            {
              'p-4': !fullHeightMobile,
              'p-0 md:p-4': fullHeightMobile,
            }
          )}
        >
          <DialogPanel
            className={clsx(
              'relative my-auto w-full min-w-0 px-5 pb-5 pt-8 md:p-8 lg:p-10 xl:p-12',
              {
                'max-w-screen-2xl': size == '2xl',
                'max-w-screen-xl': size == 'xl',
                'max-w-screen-lg': size == 'lg',
                'max-w-screen-md': size == 'md',
                'max-w-screen-sm': size == 'sm',
                'bg-neutral-200': bgDark,
                'bg-neutral-50': !bgDark,
                'min-h-full md:min-h-0 md:rounded-2xl': fullHeightMobile,
                'rounded-2xl': !fullHeightMobile,
              }
            )}
          >
            {!disableClose && (
              <button
                type="button"
                className="absolute right-0 top-0 m-3 flex size-7 items-center justify-center rounded-full border border-neutral-400 focus:outline-none"
                onClick={onClose ? onClose : handleClose}
              >
                <XMarkIcon className="size-5 text-neutral-400" />
              </button>
            )}
            {children}
          </DialogPanel>
        </div>
      </>
    </Dialog>
  )
}

export default Modal
